import { useEffect, useMemo } from "react";
import {
  GraphqlFilterRoot,
  GraphqlRelationShip,
} from "../root/general/crudGraphql/domain/models/graphqlModel";
import { useNavigate } from "react-router-dom";
import { useCrudFilter } from "../root/general/infrastructure/contexts/CrudFilterProvider";

type CrudFilterValue =
  | string
  | boolean
  | number
  | string[]
  | number[]
  | undefined;

type CrudScalerType =
  | "equal"
  | "contains"
  | "in"
  | "greaterThanOrEqual"
  | "lessThanOrEqual"
  | "between"
  | undefined;

export interface CrudFilters {
  filters: GraphqlFilterRoot;
  updateFilter: (
    key: string,
    value: CrudFilterValue,
    type?: CrudScalerType,
    fn?: (filters: GraphqlFilterRoot) => void,
    condition?: "and" | "or",
  ) => void;
  removeFilter: (
    key: string,
    fn?: (filters: GraphqlFilterRoot) => void,
  ) => void;
  getFilter: (
    key: string,
    type?: CrudScalerType,
  ) => CrudFilterValue | undefined;
  resetFilters: () => void;
  updateFilters: (
    filterProps: {
      key: string;
      value: CrudFilterValue;
      type?: CrudScalerType;
      fn?: (filters: GraphqlFilterRoot) => void;
      condition?: "and" | "or";
    }[],
  ) => void;
  removeFilters: (
    filterProps: {
      key: string;
      fn?: (filters: GraphqlFilterRoot) => void;
    }[],
  ) => void;
}

const DEFAULT_TYPE = "equal";

export default function useCrudFilters(
  filtersModel: GraphqlFilterRoot,
  paramName?: string,
): CrudFilters {
  const identifier = paramName ?? useMemo(() => crypto.randomUUID(), []);

  const { filters, setFilter } = useCrudFilter(() => {
    const searchParams = new URLSearchParams(location.search);
    const filterString = searchParams.get(identifier);
    return filterString
      ? JSON.parse(decodeURIComponent(filterString))
      : filtersModel;
  }, identifier);

  const navigate = useNavigate();

  useEffect(() => {
    if (paramName === undefined) return;
    const searchParams = new URLSearchParams(location.search);
    const filterString = encodeURIComponent(JSON.stringify(filters));
    searchParams.set(identifier, filterString);
    navigate(`?${searchParams.toString()}`, { replace: true });
  }, [filters]);

  const generate = (
    root: GraphqlFilterRoot | GraphqlRelationShip,
    key: string,
    value: CrudFilterValue,
    type = DEFAULT_TYPE,
    condition: "and" | "or",
    i = 0,
  ) => {
    if (key == "label") {
      root.label = String(value);
      return root;
    }

    const keys = key.split(".");
    if (root === undefined) root = { fields: {}, label: "" };

    root.fields = {
      ...root.fields,
      [keys[i]]: {
        ...root.fields[keys[i]],
        condition,
        ...(keys.length > 1
          ? generate(
              root.fields[keys[i]] as GraphqlRelationShip,
              keys.slice(1).join("."),
              value,
              type,
              condition,
              i++,
            )
          : {
              [type]: value,
            }),
      },
    };
    return root;
  };

  const removeKey = (
    root: GraphqlFilterRoot | GraphqlRelationShip,
    key: string,
    i = 0,
  ) => {
    if (key == "label") {
      delete root.label;
      return root;
    }
    const keys = key.split(".");
    const field = root.fields[keys[i]];
    if (field === undefined) return root;
    if ("fields" in field) {
      const result = removeKey(field, key, i + 1);
      if (Object.keys(result.fields).length == 0) {
        delete root.fields[keys[i]];
      }
    } else {
      delete root.fields[keys[i]];
    }

    return root;
  };

  const find = (
    root: GraphqlFilterRoot | GraphqlRelationShip,
    key: string,
    type: CrudScalerType = DEFAULT_TYPE,
    i = 0,
  ): CrudFilterValue => {
    if (key == "label") {
      return filters.label;
    }
    const keys = key.split(".");
    if (keys[i] === undefined || !(keys[i] in root.fields)) return undefined;

    const field = root.fields[keys[i]];

    if ("fields" in field) {
      return find(field, key, type, i + 1);
    } else {
      return field[type as keyof typeof field];
    }
  };

  const updateFilterState = (
    updateFn: (filters: GraphqlFilterRoot) => GraphqlFilterRoot,
    fn?: (filters: GraphqlFilterRoot) => void,
  ) => {
    const newFilters = updateFn({ ...filters });
    if (fn) fn(newFilters);
    setFilter(newFilters, identifier);
    return newFilters;
  };

  const updateFilter = (
    key: string,
    value: CrudFilterValue,
    type = DEFAULT_TYPE,
    fn?: (filters: GraphqlFilterRoot) => void,
    condition: "and" | "or" = "and",
  ) => {
    if (!filters.fields) return;
    return updateFilterState(
      (currentFilters) => generate(currentFilters, key, value, type, condition),
      fn,
    );
  };

  const updateFilters = (
    filterProps: {
      key: string;
      value: CrudFilterValue;
      type?: CrudScalerType;
      fn?: (filters: GraphqlFilterRoot) => void;
      condition?: "and" | "or";
    }[],
  ) => {
    return updateFilterState((currentFilters) =>
      filterProps.reduce((acc, filter) => {
        return generate(
          acc,
          filter.key,
          filter.value,
          filter.type,
          filter.condition ?? "and",
        );
      }, currentFilters),
    );
  };

  const removeFilter = (
    key: string,
    fn?: (filters: GraphqlFilterRoot) => void,
  ) => {
    return updateFilterState(
      (currentFilters) => removeKey(currentFilters, key),
      fn,
    );
  };

  const removeFilters = (
    filterProps: {
      key: string;
      fn?: (filters: GraphqlFilterRoot) => void;
    }[],
  ) => {
    return updateFilterState((currentFilters) =>
      filterProps.reduce((acc, filter) => {
        const result = removeKey(acc, filter.key);
        if (filter.fn) filter.fn(result);
        return result;
      }, currentFilters),
    );
  };

  const getFilter = (key: string, type: CrudScalerType = DEFAULT_TYPE) => {
    const filter = find(filters, key, type);

    if (typeof filter === "boolean") return filter ? 1 : 0;

    return filter;
  };

  return {
    filters: filters,
    updateFilter,
    removeFilter,
    getFilter,
    resetFilters: () => {
      setFilter(filtersModel, identifier);
    },
    updateFilters,
    removeFilters,
  };
}
